import { toast } from 'sonner'
import { useDecideInvoiceApproval } from '@/features/invoices/data/review-mutations'
import { useInvoiceApprovalCapabilities } from '@/features/invoices/queries'
import type { useUpdateInvoice } from '@/features/invoices/mutations'
import { type ApInvoiceDetail } from '@/features/invoices/view-models'
import { getApiErrorMessage } from '@/lib/api-error'

export function useInvoiceApproval(
  invoiceId: string,
  invoice: ApInvoiceDetail | undefined,
  updateInvoice: ReturnType<typeof useUpdateInvoice>,
) {
  const approvalCapabilities = useInvoiceApprovalCapabilities(invoiceId)
  const decideApproval = useDecideInvoiceApproval(invoiceId)

  // The page renders decision controls only after an invoice is available.
  const approveAccounting = () => {
    decideApproval.mutate(
      {
        decision: 'approve_accounting',
        expected_updated_at: invoice!.updated_at,
      },
      {
        onSuccess: () => toast.success('Accounting review approved'),
        onError: (err) => toast.error(getApiErrorMessage(err, 'Approval failed')),
      },
    )
  }

  const quickApprove = () => {
    updateInvoice.mutate(
      { status: 'approved_by_pc' },
      {
        onSuccess: () => toast.success('Invoice approved by PC'),
        onError: (err) => toast.error(getApiErrorMessage(err, 'Approval failed')),
      },
    )
  }

  const runApprovalDecision = (
    decision: 'approve_operations' | 'approve_finance',
    successMessage: string,
  ) => {
    decideApproval.mutate(
      { decision, expected_updated_at: invoice!.updated_at },
      {
        onSuccess: () => toast.success(successMessage),
        onError: (err) => toast.error(getApiErrorMessage(err, 'Approval failed')),
      },
    )
  }
  return {
    approvalCapabilities,
    decideApproval,
    approveAccounting,
    quickApprove,
    runApprovalDecision,
  }
}
